import React, { useEffect } from 'react';
import { t } from 'i18next';
import axios from 'axios';

import mixpanel from 'mixpanel-browser';
import { FontWeight, Heading, Text } from '@lili/lili-frontend-common';
import { BASE_URL } from '../../constants/endpoints';
import { TextWithCopy } from './components/TextWithCopy';
import WireIcon from '../../assets/icons/svg/colored-icons/ic-colored-int-wire-in.svg';
import Icon from '../../generic-components/Icon';
import { EmailSection } from './components/EmailSection';
import { useStore } from '../../store';
import { getAddressObj } from '../../helpers/address.helpers';
import { IntProviderCard } from './components/IntProviderCard';
import { StatesToolTip } from './components/StatesToolTip';
import { handleDownload } from './utils';
import { useGetIntWireInAllowedCountries } from './hooks/useGetIntWireInAllowedCountries';
import PageLayout from '../../generic-components/PageLayout/PageLayout';
import PageHeader from '../../generic-components/PageHeader/PageHeader';
import { LiliCard } from './components/LiliCard';

export const IntWireTransfer = () => {
  const HEADER = t('INT_WIRE_TRANSFER_HEADER');
  const { getPrimaryAccount, getPersonalAccountId } = useStore((state) => state);
  const getPersonalAccount = useStore((state) => state.getPersonalAccount);
  const { accountExternalId } = getPersonalAccount();
  const [isDownloading, setIsDownloading] = React.useState(false);

  const { customerId } = useStore((state) => state.currUser());
  const [openToolTip, setOpenToolTip] = React.useState(false);
  const { data: IntWireInCountryListResponse, isLoading } = useGetIntWireInAllowedCountries({
    customerId,
    accountExternalId,
  });
  useEffect(() => {
    mixpanel.track('View Incoming Int Wire Screen');
    mixpanel.track('Act on Add Money Screen', { Action: 'Incoming Int Wire', ui_screen: 'Menu Screen' });
  }, []);
  const {
    achAccountNumber,
    swiftCode,
    bankInfo: { bank, bankInstAddress, bankInstInfo, bankInstName },
  } = getPrimaryAccount();
  const splitBankAddress = getAddressObj(bank, bankInstAddress);

  const [isEmailSent, setIsEmailSent] = React.useState(false);

  const handleDownloadClick = async () => {
    setIsDownloading(true);
    const extId = getPersonalAccountId();
    const url = `${BASE_URL}/api/v1/user/${customerId}/${extId}/confirmation/internationalWire/bankAccount/download`;

    try {
      const { data } = await axios(url, {
        responseType: 'blob',
        method: 'GET',
      });
      handleDownload(URL.createObjectURL(data), 'Lili Banking International Wire Transfer Instructions.pdf');
      setIsDownloading(false);
    } catch (error) {
      console.error(error);
      setIsDownloading(false);
    }
  };

  const wireSerivceText = t('WIRE_SERVICE_TEXT', 'Wire Transfer service provided by');

  const providerHeadline = t('INT_BANKING_SERVICE');
  const providerLines = [
    {
      boldedHeader: providerHeadline,
      header: bankInstName,
      line1: bankInstInfo,
      line2: splitBankAddress.addressLine,
      line3: splitBankAddress.stateAndZip,
    },
    {
      boldedHeader: wireSerivceText,
      header: t('INT_WIRE_TT_2'),
      line1: t('INT_WIRE_TT_2_LINE_1'),
      line2: t('INT_WIRE_TT_2_LINE_2'),
      line3: t('INT_WIRE_TT_2_LINE_3'),
    },
  ];
  const disclaimer = {
    title: t('INT_WIRE_DISCLAIMER_1'),
  };
  const CardSubContentHeader = t('INT_DIRECT_DEPOSIT_PAGE_SUB_CONTENT_HEADER');
  const greyBgCardTitle = t('INT_WIRE_PAGE_SUBHEADER');

  // Extracted text for i18n
  const text1 = t('INT_WIRE_TEXT_1', 'We currently accept international wire transfer from the');
  const text2 = t('INT_WIRE_TEXT_2', 'following countries');
  const text3 = t(
    'INT_WIRE_TEXT_3',
    'Please instruct the payer to send the payment in USD. Non-USD transfers will be automatically converted and credited in USD.',
  );
  const text4 = t(
    'INT_WIRE_TEXT_4',
    '$15 fee applies per transaction.\nAdditional charges may be imposed by the sender\'s payment provider',
  );

  return (
    <PageLayout>
      <PageHeader title={HEADER} showBackButton={false} />
      <div className='flex flex-row gap-[24px] h-[78dvh]'>
        <LiliCard className='basis-[45%] ]h-full w-full p-[32px] flex flex-col justify-between'>
          <div>
            <Heading.H9 fontWeight={FontWeight.MEDIUM} id='yourAccountDetails' className='mb-[24px]'>
              {CardSubContentHeader}
            </Heading.H9>
            <TextWithCopy title={t('INT_WIRE_ACCOUNT_NUM')} text={achAccountNumber} />
            <TextWithCopy title={t('INT_WIRE_ROUT_NUM')} text={swiftCode ?? ''} />
          </div>
          <EmailSection
            setIsEmailSent={setIsEmailSent}
            isEmailSent={isEmailSent}
            isRefetching={isDownloading}
            isLoading={isDownloading}
            handleSendEmail={handleDownloadClick}
          />
        </LiliCard>
        <LiliCard className='basis-[55%] w-full flex flex-col p-[32px] gap-[24px]'>
          <div className='flex border p-[32px] border-outline-primary-bw-03 bg-surface-primary-bw-01 rounded-[8px] h-full'>
            <div className='flex flex-col'>
              <Icon iconId='calendar' className='w-[72px] h-[72px]' src={WireIcon} />
              <Heading.H9 fontWeight={FontWeight.MEDIUM} id='getPaidEarlierTitle' className='mb-[8px] mt-[16px]'>
                {greyBgCardTitle}
              </Heading.H9>
              <Text.H10 id='getPaidEarlierText' className=' text-text-main-secondary'>
                <div>
                  {text1}
                  {' '}
                  <button
                    data-tooltip-id='states-button'
                    id='states-button'
                    type='button'
                    className='text-action03 '
                    onClick={() => setOpenToolTip(!openToolTip)}
                  >
                    {text2}
                  </button>
                </div>
                <div className='mt-[10px]'>{text3}</div>
                <div className='mt-[10px]'>{text4}</div>
              </Text.H10>
            </div>
          </div>
          <IntProviderCard disclaimer={disclaimer} providerHeadline={providerHeadline} providersLines={providerLines} />
        </LiliCard>
      </div>
      {!isLoading && (
        <StatesToolTip
          allowedStates={IntWireInCountryListResponse?.allowedCountriesNames ?? []}
          openToolTip={openToolTip}
          setOpenToolTip={setOpenToolTip}
        />
      )}
    </PageLayout>
  );
};
